import { createSlice } from '@reduxjs/toolkit';

import { modelsApi } from '@/integrations/adminApi/modelsApi';
import type {
  ICreateUsedCarBody,
  IGetUsedCarsParams,
  IUsedCar,
  TCreateEquipment,
  TEquipment,
  TPackage,
  TPackageProducts,
  TStock,
  TStockCreate,
  TStockDetailed
} from '@/types/model.type';

import { createAsyncThunk } from '../create-async-thunk';

type TModelsSlice = {
  isLoading: boolean;
  total: number;

  equipments: TEquipment[];
  equipment: TEquipment | null;

  stocks: TStock[];
  stock: TStockDetailed | null;
  usedCars: IUsedCar[];
  usedCar: IUsedCar | null;

  packages: TPackage[];
  package: TPackage | null;

  products: TPackageProducts[];
  product: TPackageProducts | null;
};

const initialState: TModelsSlice = {
  isLoading: false,
  total: 0,

  equipments: [],
  equipment: null,

  stocks: [],
  stock: null,
  usedCars: [],
  usedCar: null,

  packages: [],
  package: null,

  products: [],
  product: null
};

// ------- Stocks New Cars -------
export const getStocks = createAsyncThunk(
  'admin/stocks',
  async ({
    limit,
    page,
    searchTerm,
    dealer,
    brand,
    model
  }: {
    limit?: number;
    page?: number;
    searchTerm?: string;
    dealer?: string;
    brand?: string;
    model?: string;
  }) => {
    return modelsApi.getStocks(limit, page, searchTerm, dealer, brand, model);
  }
);

export const getStockById = createAsyncThunk(
  'admin/stockId',
  async (modelId: string) => {
    return modelsApi.getStockById(modelId);
  }
);

export const createStock = createAsyncThunk(
  'admin/stocks/create',
  async (data: TStockCreate) => {
    return modelsApi.createStock(data);
  }
);

export const updateStock = createAsyncThunk(
  'admin/stocks/update',
  async ({ request_id, data }: { request_id: string; data: TStockCreate }) => {
    return modelsApi.updateStock(request_id, data);
  }
);

// ------- Stocks Used Cars -------
export const getUsedCars = createAsyncThunk(
  'admin/used-cars',
  async (params: IGetUsedCarsParams) => {
    return modelsApi.getUsedCars(params);
  }
);

export const getUsedCarById = createAsyncThunk(
  'admin/used-carId',
  async (usedCarId: string) => {
    return modelsApi.getUsedCarById(usedCarId);
  }
);

export const createUsedCar = createAsyncThunk(
  'admin/used-cars/create',
  async (data: ICreateUsedCarBody) => {
    return modelsApi.createUsedCar(data);
  }
);

export const updateUsedCar = createAsyncThunk(
  'admin/used-cars/update',
  async ({
    usedCarId,
    data
  }: {
    usedCarId: string;
    data: Partial<ICreateUsedCarBody>;
  }) => {
    return modelsApi.updateUsedCar(usedCarId, data);
  }
);

// ------- Equipments -------
export const getEquipments = createAsyncThunk(
  'admin/equipments',
  async ({
    limit,
    page,
    searchTerm,
    brand,
    model,
    isActive
  }: {
    limit?: number;
    page?: number;
    searchTerm?: string;
    brand?: string;
    model?: string;
    isActive?: boolean;
  }) => {
    return modelsApi.getEquipments(
      limit,
      page,
      searchTerm,
      brand,
      model,
      isActive
    );
  }
);

export const getEquipmentById = createAsyncThunk(
  'admin/equipmentId',
  async (equipmentId: string) => {
    return modelsApi.getEquipmentById(equipmentId);
  }
);

export const createEquipment = createAsyncThunk(
  'admin/equipments/create',
  async (data: TCreateEquipment) => {
    return modelsApi.createEquipment(data);
  }
);

export const updateEquipment = createAsyncThunk(
  'admin/equipments/update',
  async ({
    request_id,
    data
  }: {
    request_id: string;
    data: TCreateEquipment;
  }) => {
    return modelsApi.updateEquipment(request_id, data);
  }
);

// ------- Packages -------
export const getPackages = createAsyncThunk(
  'admin/packages',
  async ({
    limit,
    offset,
    searchTerm
  }: {
    limit?: number;
    offset?: number;
    searchTerm?: string;
  }) => {
    return modelsApi.getPackages(limit, offset, searchTerm);
  }
);

export const getPackageById = createAsyncThunk(
  'admin/packageId',
  async (packageId: string) => {
    return modelsApi.getPackageById(packageId);
  }
);

export const createPackage = createAsyncThunk(
  'admin/packages/create',
  async (data: TPackage) => {
    return modelsApi.createPackage(data);
  }
);

export const updatePackage = createAsyncThunk(
  'admin/packages/update',
  async ({ request_id, data }: { request_id: string; data: TPackage }) => {
    return modelsApi.updatePackage(request_id, data);
  }
);

// ------- Package Products -------
export const getPackageProducts = createAsyncThunk(
  'admin/packageProducts',
  async ({
    packageId,
    limit,
    offset,
    searchTerm
  }: {
    packageId: string;
    limit?: number;
    offset?: number;
    searchTerm?: string;
  }) => {
    return modelsApi.getPackageProducts(packageId, limit, offset, searchTerm);
  }
);

export const getPackageProductById = createAsyncThunk(
  'admin/packageProductId',
  async (packageId: string) => {
    return modelsApi.getPackageProductById(packageId);
  }
);

export const createPackageProduct = createAsyncThunk(
  'admin/packageProducts/create',
  async (data: TPackageProducts) => {
    return modelsApi.createPackageProduct(data);
  }
);

export const updatePackageProduct = createAsyncThunk(
  'admin/packageProducts/update',
  async ({
    request_id,
    data
  }: {
    request_id: string;
    data: TPackageProducts;
  }) => {
    return modelsApi.updatePackageProduct(request_id, data);
  }
);

export const adminModelsSlice = createSlice({
  name: 'admin/models',
  initialState: initialState,
  reducers: {
    resetAll: () => initialState
  },
  extraReducers: (builder) => {
    builder
      // ------- Stocks New Cars -------
      .addCase(getStocks.pending, (state) => {
        state.isLoading = true;
        state.total = 0;
      })
      .addCase(getStocks.fulfilled, (state, action) => {
        state.stocks = action.payload.data;
        state.total = action.payload.total;
        state.isLoading = false;
      })
      .addCase(getStocks.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getStockById.fulfilled, (state, action) => {
        state.stock = action.payload;
      })
      .addCase(getStockById.pending, (state) => {
        state.stock = null;
      })

      // ------- Stocks Used Cars -------
      .addCase(getUsedCars.fulfilled, (state, action) => {
        state.usedCars = action.payload.data;
        state.total = action.payload.total;
        state.isLoading = false;
      })
      .addCase(getUsedCars.pending, (state) => {
        state.isLoading = true;
        state.total = 0;
      })
      .addCase(getUsedCars.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getUsedCarById.fulfilled, (state, action) => {
        state.usedCar = action.payload;
      })
      .addCase(getUsedCarById.pending, (state) => {
        state.usedCar = null;
      })

      // ------- Equipments -------
      .addCase(getEquipments.pending, (state) => {
        state.isLoading = true;
        state.total = 0;
      })
      .addCase(getEquipments.fulfilled, (state, action) => {
        state.equipments = action.payload.data;
        state.total = action.payload.total;
        state.isLoading = false;
      })
      .addCase(getEquipments.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getEquipmentById.fulfilled, (state, action) => {
        state.equipment = action.payload;
      })
      .addCase(getEquipmentById.pending, (state) => {
        state.equipment = null;
      })

      // ------- Packages -------
      .addCase(getPackages.pending, (state) => {
        state.isLoading = true;
        state.total = 0;
      })
      .addCase(getPackages.fulfilled, (state, action) => {
        state.packages = action.payload.data;
        state.total = action.payload.total;
        state.isLoading = false;
      })
      .addCase(getPackages.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getPackageById.fulfilled, (state, action) => {
        state.package = action.payload[0] || null;
      })
      .addCase(getPackageById.pending, (state) => {
        state.package = null;
      })

      // ------- Package Products -------
      .addCase(getPackageProducts.pending, (state) => {
        state.isLoading = true;
        state.total = 0;
      })
      .addCase(getPackageProducts.fulfilled, (state, action) => {
        state.products = action.payload.data;
        state.total = action.payload.total;
        state.isLoading = false;
      })
      .addCase(getPackageProducts.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getPackageProductById.fulfilled, (state, action) => {
        state.product = action.payload;
      })
      .addCase(getPackageProductById.pending, (state) => {
        state.product = null;
      });
  }
});

export const { resetAll } = adminModelsSlice.actions;
